import type { MessageDetail } from "@mailhand/core/api";
import type { MessageState } from "@mailhand/core/schemas";
import { useState } from "react";
import { FOLLOW_UP_LABEL, MESSAGE_STATE_LABEL } from "../../app/labels.js";
import { Link } from "../../app/router.js";
import { useApi } from "../../app/services.js";
import { useTenant } from "../../app/tenant.js";
import { useTask } from "../../app/useTask.js";
import { fullTime } from "../../lib/format.js";
import { usePoll } from "../../lib/usePoll.js";
import { useResource } from "../../lib/useResource.js";
import { Badge, Button, Card, CardHeader, ErrorNote, Eyebrow, Icon, PageSpinner } from "../../ui/index.js";
import { OverrideDrawer } from "../shared/OverrideDrawer.js";
import { DISMISSED_TOAST, canDismiss, dismissMessage } from "../shared/dismiss.js";
import { OriginalMessage } from "../shared/OriginalMessage.js";
import { DecisionCard } from "./DecisionCard.js";
import { OutboundList, RunList } from "./RunList.js";
import { Timeline } from "./Timeline.js";

const POLL_MS = 5000;
const IN_FLIGHT: ReadonlySet<MessageState> = new Set(["received", "running"]);

export function MessageScreen({ id }: { id: string }) {
  const api = useApi();
  const { resource, reload } = useResource<MessageDetail>(() => api.call("messageGet", { params: { id } }), [api, id]);
  const inFlight = resource.status === "ready" && IN_FLIGHT.has(resource.data.message.state);
  usePoll(reload, POLL_MS, inFlight, false);

  return (
    <>
      <Link to="/activity" className="back-link">
        <Icon name="arrowLeft" size={14} /> Activity
      </Link>
      {resource.status === "loading" ? <PageSpinner /> : null}
      {resource.status === "error" ? <ErrorNote message={resource.message} onRetry={() => void reload()} /> : null}
      {resource.status === "ready" ? <MessageView detail={resource.data} inFlight={inFlight} reload={reload} /> : null}
    </>
  );
}

function MessageView({ detail, inFlight, reload }: { detail: MessageDetail; inFlight: boolean; reload: () => Promise<void> }) {
  const { message } = detail;
  const state = MESSAGE_STATE_LABEL[message.state];
  return (
    <>
      <header className="message-head">
        <Eyebrow live={inFlight}>{inFlight ? "Working on it" : fullTime(message.receivedAt)}</Eyebrow>
        <h1 className="page-title">{message.subject === "" ? "(no subject)" : message.subject}</h1>
        <div className="message-head-meta">
          <Badge tone={state.tone} dot>
            {state.label}
          </Badge>
          {message.followUp === "none" ? null : <Badge tone={FOLLOW_UP_LABEL[message.followUp].tone}>{FOLLOW_UP_LABEL[message.followUp].label}</Badge>}
          <span className="message-sender">
            {message.fromName === undefined ? null : <strong>{message.fromName} </strong>}
            <span className="mono muted">{message.from}</span>
          </span>
        </div>
        {message.heldReason === undefined ? null : <p className="held-reason">{message.heldReason}</p>}
      </header>
      <MessageButtons detail={detail} reload={reload} />
      <div className="message-grid">
        <div className="stack-lg">
          <Card>
            <CardHeader eyebrow="Routing Log" />
            <Timeline steps={detail.trace} />
          </Card>
          <Card>
            <CardHeader eyebrow="Original Message" />
            <OriginalMessage messageId={message.id} />
          </Card>
        </div>
        <div className="stack-lg">
          {detail.decision === null ? null : <DecisionCard decision={detail.decision} runs={detail.runs} />}
          {detail.runs.length === 0 ? null : <RunList runs={detail.runs} />}
          {detail.outbound.length === 0 ? null : <OutboundList outbound={detail.outbound} />}
        </div>
      </div>
    </>
  );
}

function MessageButtons({ detail, reload }: { detail: MessageDetail; reload: () => Promise<void> }) {
  const api = useApi();
  const { actions, reloadApprovals } = useTenant();
  const task = useTask();
  const [overrideOpen, setOverrideOpen] = useState(false);
  const id = detail.message.id;
  const waitingRun = detail.runs.find((run) => run.state === "awaiting_approval");
  const finishedRun = detail.runs.find((run) => run.state === "succeeded");

  async function decide(kind: "approve" | "reject", runId: string): Promise<void> {
    await task.run(async () => {
      if (kind === "approve") await api.call("approvalApprove", { params: { runId }, body: {} });
      else await api.call("approvalReject", { params: { runId }, body: {} });
      await Promise.all([reload(), reloadApprovals()]);
    }, kind === "approve" ? "Approved. It runs now." : "Rejected. The message is held.");
  }

  async function dismiss(): Promise<void> {
    await task.run(async () => {
      await dismissMessage(api, actions, id);
      await Promise.all([reload(), reloadApprovals()]);
    }, DISMISSED_TOAST);
  }

  async function changeFollowUp(state: "open" | "closed"): Promise<void> {
    await task.run(async () => {
      await api.call("messageFollowUp", { params: { id }, body: { state } });
      await reload();
    }, state === "closed" ? "Follow-up closed." : "Follow-up reopened.");
  }

  async function replay(): Promise<void> {
    await task.run(async () => {
      await api.call("messageReplay", { params: { id } });
      await reload();
    }, "Routing again. The result waits for your approval.");
  }

  // The tab opens inside the click, before the await, so a popup blocker lets it through.
  async function downloadOriginal(): Promise<void> {
    const tab = window.open("", "_blank");
    if (tab !== null) tab.opener = null;
    const ok = await task.run(async () => {
      const { url } = await api.call("messageRaw", { params: { id } });
      if (tab === null) window.location.assign(url);
      else tab.location.href = url;
    });
    if (!ok) tab?.close();
  }

  return (
    <div className="button-row message-buttons">
      {waitingRun === undefined ? null : (
        <>
          <Button variant="accent" busy={task.busy} onClick={() => void decide("approve", waitingRun.id)}>
            <Icon name="check" size={15} /> Approve
          </Button>
          <Button onClick={() => void decide("reject", waitingRun.id)} disabled={task.busy}>
            Reject
          </Button>
        </>
      )}
      {canDismiss(detail.message.state) ? (
        <Button onClick={() => void dismiss()} disabled={task.busy}>
          Dismiss
        </Button>
      ) : null}
      {detail.message.followUp === "open" ? (
        <Button variant="accent" busy={task.busy} onClick={() => void changeFollowUp("closed")}>
          <Icon name="check" size={15} /> Close follow-up
        </Button>
      ) : null}
      {detail.message.followUp === "closed" ? (
        <Button onClick={() => void changeFollowUp("open")} disabled={task.busy}>
          Reopen follow-up
        </Button>
      ) : null}
      <Button onClick={() => { setOverrideOpen(true); }}>Change action</Button>
      <Button variant="ghost" onClick={() => void replay()} disabled={task.busy}>
        <Icon name="replay" size={15} /> Replay
      </Button>
      <Button variant="ghost" onClick={() => void downloadOriginal()} disabled={task.busy}>
        <Icon name="download" size={15} /> Original
      </Button>
      <OverrideDrawer
        open={overrideOpen}
        onClose={() => { setOverrideOpen(false); }}
        messageId={id}
        alreadyRan={finishedRun?.actionName ?? null}
        onDone={() => {
          setOverrideOpen(false);
          void reload();
        }}
      />
    </div>
  );
}
